import { addDays, format, isValid as isValidDate, parseISO, startOfWeek } from 'date-fns';

function datePart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): string {
  return parts.find((part) => part.type === type)?.value ?? '';
}

export function isValidTimeZone(value: string | null | undefined): value is string {
  if (!value || value === 'undefined') return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

function safeTimeZone(value: string): string {
  return isValidTimeZone(value) ? value : 'UTC';
}

export function dateInTimeZone(date: Date | string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: safeTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(typeof date === 'string' ? new Date(date) : date);

  return `${datePart(parts, 'year')}-${datePart(parts, 'month')}-${datePart(parts, 'day')}`;
}

function timeZoneOffset(timeZone: string, date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA-u-nu-latn', {
    timeZone: safeTimeZone(timeZone),
    timeZoneName: 'longOffset',
  }).formatToParts(date);
  const timeZoneName = datePart(parts, 'timeZoneName');
  return timeZoneName === 'GMT' ? '+00:00' : timeZoneName.replace('GMT', '');
}

export function todayInTimeZone(timeZone: string): string {
  return dateInTimeZone(new Date(), timeZone);
}

export type DateTimeInTimeZoneOptions = Intl.DateTimeFormatOptions & {
  iso?: boolean;
};

export function formatDateTimeInTimeZone(
  iso: string,
  timeZone: string,
  options?: DateTimeInTimeZoneOptions
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const { iso: asIso = false, ...providedOptions } = options ?? {};

  if (asIso) {
    const parts = new Intl.DateTimeFormat('en-CA-u-nu-latn', {
      timeZone: safeTimeZone(timeZone),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
      timeZoneName: 'longOffset',
    }).formatToParts(date);
    const offset = timeZoneOffset(timeZone, date);

    return [
      `${datePart(parts, 'year')}-${datePart(parts, 'month')}-${datePart(parts, 'day')}`,
      `T${datePart(parts, 'hour')}:${datePart(parts, 'minute')}:${datePart(parts, 'second')}`,
      offset,
    ].join('');
  }

  const formatOptions =
    Object.keys(providedOptions).length > 0
      ? providedOptions
      : { dateStyle: 'medium', timeStyle: 'short' } satisfies Intl.DateTimeFormatOptions;
  return new Intl.DateTimeFormat(undefined, {
    ...formatOptions,
    timeZone: safeTimeZone(timeZone),
  }).format(date);
}

export function formatTimeInTimeZone(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    timeZone: safeTimeZone(timeZone),
    timeStyle: 'short',
  }).format(date);
}

export function formatLocalDate(date: Date | string): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'yyyy-MM-dd');
}

/** Return today in the viewer device's local time zone. */
export function getLocalToday(): string {
  return formatLocalDate(new Date());
}

export function addLocalDays(date: Date | string, days: number): string {
  const d = typeof date === 'string' ? parseISO(date) : new Date(date);
  return format(addDays(d, days), 'yyyy-MM-dd');
}

export function isValidDateOnly(value: string): boolean {
  if (!/^[1-9]\d{3}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = parseISO(value);
  return isValidDate(parsed) && format(parsed, 'yyyy-MM-dd') === value;
}

/**
 * Display a date-only string (``YYYY-MM-DD``) as a calendar date.
 *
 * ``new Date('2026-10-07')`` is parsed as UTC midnight and renders a day
 * early west of UTC; ``parseISO`` parses date-only strings at *local*
 * midnight, so the calendar day survives. Not for timestamps.
 */
export function formatDateOnly(
  value: string | null | undefined,
  fallback = '—',
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return fallback;
  return parseISO(value).toLocaleDateString(undefined, options);
}

function firstInstantAtOrAfterLocalDate(dateOnly: string, timeZone: string): number {
  const [year, month, day] = dateOnly.split('-').map(Number);
  let earliest = Date.UTC(year, month - 1, day) - 36 * 60 * 60 * 1000;
  let latest = Date.UTC(year, month - 1, day) + 36 * 60 * 60 * 1000;

  while (earliest < latest) {
    const candidate = earliest + Math.floor((latest - earliest) / 2);
    if (dateInTimeZone(new Date(candidate), timeZone) < dateOnly) {
      earliest = candidate + 1;
    } else {
      latest = candidate;
    }
  }

  return earliest;
}

export function startOfDayInTimeZone(dateOnly: string, timeZone: string): string {
  const earliest = firstInstantAtOrAfterLocalDate(dateOnly, timeZone);
  if (dateInTimeZone(new Date(earliest), timeZone) !== dateOnly) {
    throw new RangeError(`The date ${dateOnly} does not exist in ${timeZone}`);
  }

  return new Date(earliest).toISOString();
}

export function startOfNextDayInTimeZone(dateOnly: string, timeZone: string): string {
  const nextDay = format(addDays(parseISO(dateOnly), 1), 'yyyy-MM-dd');
  return new Date(firstInstantAtOrAfterLocalDate(nextDay, timeZone)).toISOString();
}

export function endOfDayInTimeZone(dateOnly: string, timeZone: string): string {
  return new Date(new Date(startOfNextDayInTimeZone(dateOnly, timeZone)).getTime() - 1)
    .toISOString()
    .replace('.999Z', '.999999Z');
}

export function toIsoFromDatetimeLocal(value: string): string {
  return value ? new Date(value).toISOString() : '';
}

export function getLocalWeekStart(date: Date = new Date()): string {
  return format(startOfWeek(date, { weekStartsOn: 0 }), 'yyyy-MM-dd');
}
